/**
 * The Notifications page — every fact it shows, and every call it makes.
 *
 * Spec 2026-09-22 §4.5, task 5.2. The endpoints are task 5.1, already on this
 * branch: `backend/src/routes/admin-notifications.ts` is the contract, and the
 * shapes below were copied from it field by field, never guessed.
 *
 * ── WHY THE LOGIC LIVES IN A `.ts` FILE AND NOT IN THE COMPONENTS ──────────
 * Root jest compiles no `.tsx` (its ts-jest config sets no `jsx` option, and
 * a `.tsx` import fails with `SyntaxError: Unexpected token '<'` — measured,
 * not assumed). So everything with a rule in it lives here, where a test can
 * EXECUTE it, and the `.tsx` files hold only markup. This mirrors the agent
 * page (`answer-view.ts`, `protocol.ts`, `turn-state.ts` beside `index.tsx`).
 *
 * ── NO `lucide-react`, NO `@/lib/utils` IN THIS PAGE ───────────────────────
 * Both live ONLY in `frontend/admin/node_modules`, which a root jest run is
 * not guaranteed to have. `@/lib/utils` also pulls in clsx + tailwind-merge.
 * Importing either would make this page's tests depend on an install that CI
 * does not do for the admin package. The number formatter below is the same
 * `Intl.NumberFormat('en-US')` that `formatNumber` uses.
 *
 * ── THE TWO GRAINS ARE CARRIED, NOT FLATTENED ─────────────────────────────
 * The route file is emphatic about this and the table must not undo it. Six
 * numbers count CARDS (one `core.notification_center` row each) and five count
 * EMAIL EVENTS (`answered`/`delivered`/`bounced` are DISTINCT-message-id
 * counts, `complaints`/`clicks_human` are one `core.email_events` row each —
 * Task 7.9). A ratio across the two grains is meaningless — `delivered /
 * email_sent` looks like a delivery rate and is not one, because a bounce or
 * a click can carry `notification_id NULL` and belongs to no card. So every
 * column declares its grain, every ratio names a denominator OF THE SAME
 * GRAIN, and `columnsWithMixedGrainRatios()` exists so a test can prove that
 * rule still holds after somebody edits the table.
 *
 * ── "delivered" excludes a message that bounced after acceptance (Task 7.9) ──
 * SES can report a Delivery and then a Bounce for the SAME message — measured
 * on PROD 2026-09-24: 4 of 51 messages with a delivery event bounced
 * afterwards. `answered` (new) is the messages SES gave a final verdict for
 * — a delivery or a bounce — and is the denominator for both, so the Bounced
 * ratio can never read above 100 % the way `bounced / delivered` could.
 * `delivered + bounced === answered` holds whenever a message's bounce row
 * lands in the SAME window and stage as its delivery row (measured on PROD
 * 2026-09-25: always, for every stage, over all time); where it does not,
 * the two can only under-count `answered`, never exceed it.
 */
import { adminFetch, AdminApiError } from '@/providers/admin-api';

/* ═════════════════════════════════════════════════════════════════════════
 * Paths
 * ═════════════════════════════════════════════════════════════════════════ */

/**
 * Written as quoted literals on purpose. `tests/unit/lint/admin-fetch-route-exists.test.ts`
 * scans this tree for `'/v1/admin/...'` strings and fails when one resolves to
 * no route in `backend/src/index.ts`. A path built only as
 * `` `${apiUrl}/v1/admin/x` `` carries no quote before the slash, so that lint
 * never sees it — the page would be unguarded against the exact seam defect
 * (a page fetching an unrouted URL) the lint was written for.
 */
export const NOTIFICATION_PATHS = {
  overview: '/v1/admin/notifications/overview',
  outbox: '/v1/admin/notifications/outbox',
  controls: '/v1/admin/notifications/controls',
  holdout: '/v1/admin/notifications/holdout',
} as const;

/* ═════════════════════════════════════════════════════════════════════════
 * Shapes — copied from backend/src/routes/admin-notifications.ts
 * ═════════════════════════════════════════════════════════════════════════ */

export const OVERVIEW_WINDOWS = [7, 30, 90] as const;
export type OverviewWindowDays = (typeof OVERVIEW_WINDOWS)[number];
export const DEFAULT_WINDOW: OverviewWindowDays = 30;

export interface StageOverviewRow {
  stage: string;
  cards: number;
  read: number;
  push_sent: number;
  receipt_ok: number;
  email_sent: number;
  resolved_72h: number;
  answered: number;
  delivered: number;
  bounced: number;
  complaints: number;
  clicks_human: number;
}

/** 🔴 `window_days`, not `days`. The route returns `window_days`; the task
 *  brief said `days`. The route file is the contract. */
export interface OverviewData {
  window_days: OverviewWindowDays;
  stages: StageOverviewRow[];
}

export interface OutboxBucket {
  rail: string;
  reason: string;
  count: number;
}

/** The two terminal states a row can reach WITHOUT sending. */
export type OutboxEndedState = 'exhausted' | 'cancelled';

/**
 * A bucket of rows that ENDED WITHOUT SENDING. Grouped by `state` and
 * `outcome`, the TERMINAL cause, with `reason` — the reason the send was
 * DEFERRED — beside it.
 *
 * The panel used to group on `reason` alone, so it answered "what was holding
 * this message" where the operator asked "why did it die". `no_address`,
 * `opted_out` and `attempts` are a data gap, a member's own choice and a
 * failing rail; all three used to read as "Quiet hours". Mirrors
 * `ExhaustedBucket` in backend/src/routes/admin-notifications.ts, which carries
 * the full note (the name is the route's, kept so its fields do not move).
 *
 * `state` arrived with issue #3076: `exhausted` = the rail GAVE UP,
 * `cancelled` = the row was retired with a stated reason. The panel used to
 * read `exhausted` alone and so could never show a cancelled row.
 *
 * `outcome` is nullable because the column is.
 */
export interface ExhaustedBucket {
  rail: string;
  state: OutboxEndedState;
  outcome: string | null;
  reason: string;
  count: number;
}

/**
 * `most_overdue_seconds` and `ended_*` arrived with Task 7.6 / issue #3076.
 * They are typed as possibly ABSENT (`?`) on purpose: the admin bundle and the
 * API deploy in separate steps, so this bundle can meet an API older than
 * itself (a rolled-back API, for one). A field that API does not send must read
 * as "not reported", never as "nothing".
 * `outboxOverdueView` and the card's ended list both make that distinction.
 */
export interface OutboxData {
  pending: OutboxBucket[];
  ended_24h?: ExhaustedBucket[];
  ended_last_24h_total?: number;
  exhausted_24h: ExhaustedBucket[];
  exhausted_last_24h_total: number;
  /** Informational only — see `OUTBOX_COPY.oldestWaitingNote`. Never judged. */
  oldest_pending_age_seconds: number | null;
  /** How far the most overdue DUE row is past its send time. NULL = none is. */
  most_overdue_seconds?: number | null;
}

export interface StageControlRow {
  stage: string;
  email_paused: boolean;
  email_paused_at: string | null;
  email_pause_reason: string | null;
  paused_by: string | null;
  resumed_at: string | null;
  resumed_by: string | null;
  updated_at: string | null;
}

export interface ControlsData {
  stages: StageControlRow[];
  paused_count: number;
}

export interface HoldoutData {
  arms: { arm: string; members: number }[];
  total: number;
}

/* ═════════════════════════════════════════════════════════════════════════
 * Tiles — a read that failed says so, and shows no number
 * ═════════════════════════════════════════════════════════════════════════ */

/**
 * The same `{status, error}` shape System Health's backend stamps on each of
 * its tiles, so `isUnavailable` / `tileShell` / `TileUnavailable` work here
 * unchanged. The difference is WHO stamps it: System Health's API reports a
 * tile it could not read, while these four endpoints either answer or throw.
 * So the page stamps it from the outcome of the call. The meaning is the one
 * U29 fixed: an unreadable tile shows an em-dash, never a zero, because a zero
 * is a measurement and "unavailable" is the absence of one.
 */
export interface Tile<T> {
  status: 'ok' | 'unavailable';
  error: string | null;
  data: T | null;
}

/**
 * What a card should put in its body right now.
 *
 * All four cards ask this ONE function, so "still reading" and "read, and the
 * answer is nothing" cannot diverge between them. The distinction is the same
 * one U29 drew: an em-dash and a friendly "nothing here" are both STATEMENTS
 * about a completed read, and neither may appear while the read is in flight.
 *
 * `data === null` with no load running is not a normal state; it is reported
 * as unavailable rather than as a permanent "Loading…", so a card can never
 * sit and claim to be working when nothing is.
 */
export type TileBody = 'loading' | 'unavailable' | 'ready';

export function tileBody<T>(tile: Tile<T>, loading: boolean): TileBody {
  if (tile.status === 'unavailable') return 'unavailable';
  if (tile.data !== null) return 'ready';
  return loading ? 'loading' : 'unavailable';
}

export function loadingTile<T>(): Tile<T> {
  return { status: 'ok', error: null, data: null };
}

export function describeFetchError(e: unknown): string {
  if (e instanceof AdminApiError) return `HTTP ${e.status}: ${e.message}`;
  const message = (e as Error)?.message;
  return message && message.length > 0 ? message : 'The request failed.';
}

/**
 * Runs one read and wraps its outcome. It never throws: one dead tile must not
 * blank the other three, which is why the page fires four independent reads
 * rather than one combined load with a single catch.
 */
export async function readTile<T>(run: () => Promise<Response>): Promise<Tile<T>> {
  try {
    const res = await run();
    const body = (await res.json()) as { ok?: boolean; data?: T };
    if (!body || body.ok !== true || body.data === undefined || body.data === null) {
      return { status: 'unavailable', error: 'The response carried no data.', data: null };
    }
    return { status: 'ok', error: null, data: body.data };
  } catch (e) {
    return { status: 'unavailable', error: describeFetchError(e), data: null };
  }
}

export function fetchOverview(apiUrl: string, days: OverviewWindowDays): Promise<Tile<OverviewData>> {
  return readTile<OverviewData>(() =>
    adminFetch(`${apiUrl}${NOTIFICATION_PATHS.overview}?days=${days}`),
  );
}

export function fetchOutbox(apiUrl: string): Promise<Tile<OutboxData>> {
  return readTile<OutboxData>(() => adminFetch(`${apiUrl}${NOTIFICATION_PATHS.outbox}`));
}

export function fetchControls(apiUrl: string): Promise<Tile<ControlsData>> {
  return readTile<ControlsData>(() => adminFetch(`${apiUrl}${NOTIFICATION_PATHS.controls}`));
}

export function fetchHoldout(apiUrl: string): Promise<Tile<HoldoutData>> {
  return readTile<HoldoutData>(() => adminFetch(`${apiUrl}${NOTIFICATION_PATHS.holdout}`));
}

/* ═════════════════════════════════════════════════════════════════════════
 * The write
 * ═════════════════════════════════════════════════════════════════════════ */

export type ControlAction = 'pause' | 'resume';

/** The stage + direction a confirm dialog is open for. */
export interface PendingControlView {
  stage: string;
  action: ControlAction;
}

/** Pause needs a reason: it is what the audit row and the next operator read. */
export const PAUSE_REASON_REQUIRED = 'Give a reason — it is written to the audit log.';

export function validateControlSubmission(
  action: ControlAction,
  reason: string,
): string | null {
  if (action === 'pause' && reason.trim().length === 0) return PAUSE_REASON_REQUIRED;
  if (reason.trim().length > 500) return 'The reason must be 500 characters or fewer.';
  return null;
}

/**
 * `POST /v1/admin/notifications/controls/{stage}`.
 *
 * The stage is encoded because it is a path segment the API matches with
 * `([^/]+)` and then `decodeURIComponent`s. An empty reason is OMITTED rather
 * than sent as `""`: the endpoint's Joi schema is `min(1)`, so an empty string
 * is a 400. A resume's reason is optional — unlike pause, `validateControlSubmission`
 * lets a resume through with none typed — but when the operator DOES type one
 * it is sent the same way pause's is, and the backend writes it into the
 * resume audit row's `details.reason` (Task 7.8).
 */
export async function submitStageEmailControl(
  apiUrl: string,
  stage: string,
  action: ControlAction,
  reason: string,
): Promise<StageControlRow> {
  const trimmed = reason.trim();
  const body: { action: ControlAction; reason?: string } = { action };
  if (trimmed.length > 0) body.reason = trimmed;

  const res = await adminFetch(
    `${apiUrl}${NOTIFICATION_PATHS.controls}/${encodeURIComponent(stage)}`,
    { method: 'POST', body: JSON.stringify(body) },
  );
  const parsed = (await res.json()) as { ok?: boolean; data?: StageControlRow };
  if (!parsed || parsed.ok !== true || !parsed.data) {
    throw new Error('The control was not confirmed by the API.');
  }
  return parsed.data;
}

/* ═════════════════════════════════════════════════════════════════════════
 * The table
 * ═════════════════════════════════════════════════════════════════════════ */

export type Grain = 'card' | 'event';

export type OverviewCountKey = Exclude<keyof StageOverviewRow, 'stage'>;

export interface RailsColumn {
  key: OverviewCountKey;
  /** Plain words. A raw metric name never reaches the screen. */
  header: string;
  grain: Grain;
  /** The count this one is a share of — ALWAYS of the same grain, or null. */
  denominator: OverviewCountKey | null;
  /** One sentence for the column's tooltip/caption. */
  meaning: string;
}

/**
 * 🔴 ORDERED BY GRAIN, AND THE ORDER IS LOAD-BEARING.
 *
 * Every `card` column precedes every `event` column, so the header's grain
 * bands are two contiguous runs. The first version of this table had
 * `resolved_72h` (a CARD count) last, after the four event columns, while the
 * header drew a 6-wide band and then a 4-wide band — so `Delivered` rendered
 * under "Cards" and `Resolved ≤72 h` under "Email events". The table said the
 * exact opposite of the thing it exists to say. `grainBands()` now DERIVES the
 * bands from this list instead of assuming a shape, so a future reorder cannot
 * lie again; keeping the grains contiguous is what keeps it to two bands.
 */
export const RAILS_COLUMNS: readonly RailsColumn[] = Object.freeze([
  { key: 'cards', header: 'Cards', grain: 'card', denominator: null,
    meaning: 'Notification cards created in the window.' },
  { key: 'read', header: 'Read', grain: 'card', denominator: 'cards',
    meaning: 'Cards the member opened.' },
  { key: 'push_sent', header: 'Push sent', grain: 'card', denominator: 'cards',
    meaning: 'Cards that also sent a push.' },
  { key: 'receipt_ok', header: 'Receipts OK', grain: 'card', denominator: 'push_sent',
    meaning: 'Pushes Expo confirmed as delivered.' },
  { key: 'email_sent', header: 'Emails sent', grain: 'card', denominator: 'cards',
    meaning: 'Cards that also sent an email.' },
  { key: 'resolved_72h', header: 'Resolved ≤72 h', grain: 'card', denominator: 'cards',
    meaning: 'Cards whose task the member finished within 72 hours.' },
  { key: 'answered', header: 'Answered', grain: 'event', denominator: null,
    meaning: 'Emails the mail provider gave a final answer for: delivered or bounced.' },
  { key: 'delivered', header: 'Delivered', grain: 'event', denominator: 'answered',
    meaning: 'Emails delivered that did not bounce afterwards.' },
  { key: 'bounced', header: 'Bounced', grain: 'event', denominator: 'answered',
    meaning: 'Emails that bounced, including a bounce after the provider first accepted the email.' },
  { key: 'complaints', header: 'Complaints', grain: 'event', denominator: 'delivered',
    meaning: 'Spam complaints. These drive the breaker in wave 7.' },
  { key: 'clicks_human', header: 'Clicks', grain: 'event', denominator: 'delivered',
    meaning: 'Clicks by a person, with scanner clicks excluded.' },
]);

/** What a grain is called above the columns that carry it. */
export const GRAIN_BAND_LABELS: Record<Grain, string> = {
  card: 'Cards',
  event: 'Email events',
};

export const GRAIN_BAND_MEANINGS: Record<Grain, string> = {
  card: 'One unit is one notification card.',
  event: 'One unit is one email or one email event. Not comparable with the card counts.',
};

export interface GrainBand {
  grain: Grain;
  label: string;
  meaning: string;
  /** How many columns this band spans — a `colSpan`. */
  span: number;
}

/**
 * The header bands, DERIVED from the columns as contiguous runs of one grain.
 *
 * Never a pair of counted totals: `RAILS_COLUMNS.filter(card).length` is 6
 * whether or not those six are adjacent, so a band drawn from a total is right
 * only by luck of the ordering, and wrong silently when the ordering changes.
 * A run cannot be wrong about which columns it covers, because it IS the run.
 */
export function grainBands(columns: readonly RailsColumn[] = RAILS_COLUMNS): GrainBand[] {
  const bands: GrainBand[] = [];
  for (const column of columns) {
    const last = bands[bands.length - 1];
    if (last && last.grain === column.grain) {
      last.span += 1;
      continue;
    }
    bands.push({
      grain: column.grain,
      label: GRAIN_BAND_LABELS[column.grain],
      meaning: GRAIN_BAND_MEANINGS[column.grain],
      span: 1,
    });
  }
  return bands;
}

/**
 * Columns whose ratio crosses the card/event line. MUST be empty.
 *
 * Exported so the rule is proved by execution rather than by this comment.
 */
export function columnsWithMixedGrainRatios(
  columns: readonly RailsColumn[] = RAILS_COLUMNS,
): string[] {
  const grainOf = new Map(columns.map((c) => [c.key, c.grain]));
  return columns
    .filter((c) => c.denominator !== null && grainOf.get(c.denominator) !== c.grain)
    .map((c) => `${c.key} / ${c.denominator}`);
}

const NUMBER = new Intl.NumberFormat('en-US');

export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return NUMBER.format(value);
}

/**
 * `n / d` with a percentage, or null when there is no percentage to state.
 *
 * A zero denominator returns null rather than "0%": nothing was sent, so no
 * share of it exists. Printing 0% would read as a measured failure.
 */
export function formatRate(numerator: number, denominator: number): string | null {
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator <= 0) return null;
  const pct = (numerator / denominator) * 100;
  const shown = pct >= 10 || pct === 0 ? pct.toFixed(0) : pct.toFixed(1);
  return `${NUMBER.format(numerator)} / ${NUMBER.format(denominator)} · ${shown}%`;
}

/**
 * `stage_email` → `Stage email`.
 *
 * Used for the outbox's rail and reason codes, which are open-ended: the API
 * reports whatever the outbox holds, so no fixed map can cover them. The raw
 * code is kept in the cell's `title`, because an operator who reads a reason
 * here goes on to grep the logs for it and a prettified string would not match.
 */
export function humanLabel(value: string): string {
  const words = value.replace(/[_-]+/g, ' ').trim();
  if (words.length === 0) return '—';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Human label for a stage key. Unknown keys de-snake rather than disappear. */
export function stageLabel(stage: string): string {
  const known: Record<string, string> = {
    welcome: 'Welcome',
    onboarding_incomplete: 'Onboarding incomplete',
    photos_missing: 'Photos missing',
    preferences_missing: 'Preferences missing',
    identity_verification: 'Identity verification',
    match_ready: 'Match ready',
    waiting_for_match: 'Waiting for match',
    new_introduction: 'New introduction',
    match_reminder: 'Match reminder',
    interest_expressed: 'Interest expressed',
    mutual_match: 'Mutual match',
    payment_abandonment: 'Payment abandonment',
    credit_expiring: 'Credit expiring',
    dormant_user: 'Dormant member',
    post_connection: 'After the connection',
    verification_retry: 'Verification retry',
  };
  return known[stage] ?? humanLabel(stage);
}

export function formatAge(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)} h`;
  return `${(seconds / 86400).toFixed(1)} days`;
}

/* ═════════════════════════════════════════════════════════════════════════
 * 🔴 The honest note — wave 7, now TRUE
 * ═════════════════════════════════════════════════════════════════════════ */

/**
 * 🔴 THESE SENTENCES CHANGED WITH THE CODE, IN THE SAME COMMIT (Task 7.1).
 *
 * They used to say the pause did NOT stop a send, because it did not:
 * `sendStageEmail` ignored `core.notification_stage_controls`. It reads it now
 * and returns the NotSent reason `stage_paused`, so the old wording became the
 * false one. A page that under-claims is exactly as wrong as a page that
 * over-claims — an operator who believes the control is inert keeps hunting for
 * another way to stop the rail, and the one they reach for next is the
 * environment kill switch, which stops EVERY stage.
 *
 * The words live here, and `StageControls.tsx` renders them from here, so a
 * second hand-typed copy cannot drift from the behaviour. The test forbids one.
 */
export const PAUSE_CONTROL_LABEL = 'Pause stage email (stops it on the next send)';

/** The note's heading. A constant for the same reason as the note itself. */
export const WAVE_7_NOTE_HEADING = 'Pausing stops that stage’s email.';

export const WAVE_7_NOTE =
  'Pausing stops that stage’s email on the next send, for every member — the in-app card ' +
  'and the push are unaffected, and a queued email for that stage is cancelled. It also writes ' +
  'the control row, the audit entry and the metric. Nothing resumes a stage by itself: a stage ' +
  'the complaint breaker paused (paused by "breaker") stays paused until a person resumes it ' +
  'here, and after a breaker pause that decision waits until the complaints are understood.';

/* ═════════════════════════════════════════════════════════════════════════
 * 🔴 The holdout card's words — wave 7, CORRECTED
 * ═════════════════════════════════════════════════════════════════════════ */

/**
 * 🔴 THESE SENTENCES WERE FALSE UNTIL 2026-09-23, and they were false in the
 * direction that matters: they over-claimed.
 *
 * The card labelled the arm as one that receives nothing, and described it as
 * the group the rail never writes to. Neither is true. A holdout member
 * receives every in-app card, every push, and every
 * CRITICAL email — a new introduction, a mutual match, an expiring deadline.
 * The arm withholds ONE thing: non-critical lifecycle EMAIL
 * (`stage-email.ts` consults it only when `classifyStage(stage) ===
 * 'noncritical'`). An operator reading the old words would have taken a
 * holdout member's silence for a bug, or — worse — believed the experiment
 * measured the whole rail when it measures the nudge email alone.
 *
 * The words live here and `HoldoutCard.tsx` renders them from here, for the
 * same reason the pause sentences do: a second hand-typed copy can be edited
 * while the constant stays put. The test forbids one, and forbids the old
 * sentences anywhere in the page.
 */
export const HOLDOUT_ARM_LABELS: Record<string, string> = {
  holdout: 'Holdout — no nudge email',
  treatment: 'Treatment — receives nudge email',
};

export const HOLDOUT_SUBTITLE =
  'The group that receives no non-critical lifecycle email. Cards, pushes and critical emails ' +
  'still go. It is what the nudge-email numbers on this page are compared against.';

/**
 * The third tile's label and the sentence under it.
 *
 * "Assigned" on its own reads as "members in the experiment", which invites the
 * reading "everybody". It is not: an arm is minted at the moment a member
 * reaches the holdout consult inside a non-critical send — after the address,
 * the environment, the tester check, the pause and `already_sent` have all
 * passed — so the number counts members who reached a MAILABLE non-critical
 * send, once, and never grows for them again. Testers are never given an arm.
 */
export const HOLDOUT_ASSIGNED_LABEL = 'Assigned';

export const HOLDOUT_ASSIGNED_NOTE =
  'Members who reached a mailable non-critical send. The arm is minted once, then never changes.';

/* ═════════════════════════════════════════════════════════════════════════
 * 🔴 The Outbox card's words — Task 7.6 and issue #3076
 * ═════════════════════════════════════════════════════════════════════════ */

/**
 * 🔴 THE CARD'S WARNING WAS A FALSE ALARM BY CONSTRUCTION, UNTIL 2026-09-24.
 *
 * It turned amber when the OLDEST pending row was older than a quarter of an
 * hour, and said the rail might not be draining. But every pending row waits
 * ON PURPOSE until its own `next_attempt_at`: quiet hours hold a push until
 * 08:00 in the member's zone, `cadence_capped` and `noncritical_24h_cap` hold
 * a row up to ~24 h, and a closed email rail (`env_skipped`) holds one up to
 * 30 days. So the warning showed almost all the time. Measured 2026-09-24 at
 * 08:40Z with this endpoint's own SQL: PREDEV held 1 pending row, 16.1 h old,
 * and PROD 8, the oldest 7.1 h old — and on BOTH, not one row was past its send
 * time. The old card was amber on both. A warning that is always on is read as
 * furniture, and then it is not read on the day it is true.
 *
 * The card now judges ONE number: how far the most overdue DUE row is past its
 * send time (`most_overdue_seconds`). The oldest age is still shown, as
 * information, under a sentence that says why it is not a fault.
 *
 * The words live here and `components/OutboxCard.tsx` renders them from here,
 * for the reason the pause and holdout sentences do: a second hand-typed copy
 * can be edited while the constant stays put. The test forbids one, and forbids
 * the old warning anywhere in the page.
 */

/**
 * 135 minutes: TWO SWEEP PERIODS PLUS A QUARTER OF AN HOUR — derived from the
 * mechanism, not chosen.
 *
 *   • The drain runs only inside the warm-fuzzy scheduler sweep, and the sweep
 *     is HOURLY (rate(1 hour)): `events.Schedule.rate(cdk.Duration.hours(1))`
 *     on `udc-{env}-warm-fuzzy-scheduler` (infrastructure/lib/stacks/
 *     api-stack.ts, `WarmFuzzySchedulerRule`). The drain selects due rows
 *     against the sweep's START instant (`now` in `runWarmFuzzyScheduler`, not
 *     the moment the drain begins), so a row that falls due one second after a
 *     sweep started — even while that sweep's stage loop is still running —
 *     waits for the NEXT sweep: up to 60 min past its send time on a healthy
 *     rail. That wait is the design.
 *   • ONE skipped drain is also normal. The drain does not start, or stops
 *     mid-batch, when the sweep has less than `OUTBOX_MIN_BUDGET_MS` left
 *     (`NotificationOutbox.BudgetStoppedTotal`), and runbook §4d reads a single
 *     stopped sweep as "not a finding — the next one drains". That sweep costs
 *     the row a second period: up to 60 min more.
 *   • +15 min is margin for the sweeps' own start and run time: the measured
 *     PROD sweep is p95 941 ms (runbook §4d), so 15 min is generous.
 *
 * So past 135 min, at least TWO hourly sweeps have passed since the row fell
 * due and neither drained it. That is always worth a look, and it has five
 * causes, which runbook §4d lists and tells apart: the sweep did not run (the
 * rule targets the Lambda with `retryAttempts: 0`, so a failed invocation waits
 * a whole hour); the drain stopped for budget on two sweeps in a row; more rows
 * were due than the batches took (`OUTBOX_BATCH_LIMIT` = 50 per sweep); an item
 * threw (`outbox drain: item failed`); or the stage predicate answered "could
 * not tell" and the row keeps its wake-up time until `expires_at`. The card's
 * sentence names none of them, because it cannot tell which one it is.
 *
 * 🔴 IT WAS 75 MIN (60 + 15) IN THE FIRST VERSION OF THIS CHANGE, and that
 * tolerated ZERO budget stops: one stopped sweep — which §4d calls not a
 * finding — would have turned the card amber. That is the always-on-warning
 * shape this constant exists to remove, one step smaller.
 */
export const STALE_OVERDUE_SECONDS = 135 * 60;

export const OUTBOX_COPY = {
  subtitle:
    'Waiting is counted by what holds it; ended is counted by what ended it. ' +
    'No message content leaves the API.',
  waitingHeading: 'Waiting to send',
  nothingWaiting: 'Nothing is waiting.',
  endedHeading: 'Ended without sending (last 24 h)',
  nothingEnded: 'Nothing ended without sending.',
  /** A row the rail GAVE UP on (`state = 'exhausted'`), marked on its line. */
  gaveUpTag: 'Gave up',
  /** A row that ended without recording a cause. Said, never hidden. */
  noOutcome: 'Cause not recorded',
  overdueHeading: 'Past its send time',
  nothingOverdue: 'Nothing is past its send time.',
  overdueWithinSweep:
    `Due, and waiting for an hourly sweep. Normal below ${STALE_OVERDUE_SECONDS / 60} min — ` +
    'one sweep may skip the outbox when it runs short of time.',
  /** The API did not send the field (an older API than this page). Not "none". */
  notReported: 'Not reported by the API.',
  oldestWaitingLabel: 'Oldest waiting',
  oldestWaitingNote:
    'Rows wait on purpose until their send time — quiet hours, the 24 h caps or a closed ' +
    'email rail — so a large age here is not a fault.',
} as const;

/**
 * The amber sentence. A function because it carries the measured age.
 *
 * It states only what the number proves — two hourly sweeps passed and the
 * outbox did not drain the row — and names NO cause. There are five (see
 * STALE_OVERDUE_SECONDS) and the card cannot tell them apart; naming one would
 * send an operator after the wrong fault. §4d lists them in the order to check.
 */
export function outboxOverdueWarning(seconds: number): string {
  return (
    `A row is ${formatAge(seconds)} past its send time — two hourly sweeps passed and the ` +
    'outbox did not drain it. The ops runbook §4d lists the causes.'
  );
}

export type OutboxOverdueKind = 'not_reported' | 'none' | 'within_sweep' | 'stale';

export interface OutboxOverdueView {
  kind: OutboxOverdueKind;
  /** The overdue age, when there is one. */
  seconds: number | null;
  /** What the card says under the heading. */
  sentence: string;
}

/**
 * The card's ONE judgement. `oldest_pending_age_seconds` is deliberately not
 * read here — see STALE_OVERDUE_SECONDS for why it cannot mean "stuck".
 *
 * `undefined` (the field absent) and `null` (the API measured, and nothing is
 * due) are different answers, and only the second may print "Nothing is past
 * its send time." — a sentence about a read that happened.
 */
export function outboxOverdueView(
  data: Pick<OutboxData, 'most_overdue_seconds'> | null | undefined,
): OutboxOverdueView {
  const raw = data?.most_overdue_seconds;
  if (raw === null) return { kind: 'none', seconds: null, sentence: OUTBOX_COPY.nothingOverdue };
  if (typeof raw !== 'number' || !Number.isFinite(raw)) {
    return { kind: 'not_reported', seconds: null, sentence: OUTBOX_COPY.notReported };
  }
  if (raw > STALE_OVERDUE_SECONDS) {
    return { kind: 'stale', seconds: raw, sentence: outboxOverdueWarning(raw) };
  }
  return { kind: 'within_sweep', seconds: raw, sentence: OUTBOX_COPY.overdueWithinSweep };
}

/**
 * What each terminal outcome MEANS, in plain words.
 *
 * 🔴 CLOSED, AND HELD TO THE BACKEND'S OWN LISTS. The keys must be exactly the
 * union of `OUTBOX_EXHAUSTED_OUTCOMES` and `OUTBOX_CANCELLED_OUTCOMES` in
 * backend/src/notifications/outbox-outcomes.ts, which the compiler holds to the
 * types `exhaust()`, `cancel()` and `cancelPendingForStage()` are declared
 * with. The page test fails on a missing key AND on an extra one, so a new
 * refusal cannot reach this card unexplained and a misspelt key cannot sit here
 * matching nothing. An outcome not in the map still renders — as its raw code —
 * because a label map must never hide a row.
 *
 * The words end in the operator's verdict where there is one: "correct" (the
 * system did the right thing), "a data gap" or "a fault" (something to fix),
 * "by design off PROD" (expected outside production). "gave up" is used for
 * the three `exhausted` outcomes and for nothing else.
 */
export const OUTBOX_OUTCOME_MEANINGS: Record<string, string> = {
  aged_out: 'gave up — it expired before it could go',
  attempts: 'gave up — it ran out of attempts',
  retry_after_expiry: 'gave up — its retry after a send failure would come after it expired',
  member_gone: 'member deleted, suspended, or a PROD tester — correct',
  stage_resolved: 'no longer applies — correct',
  id_verification_required: 'the member must verify their ID before they can answer — the ID-verification nudge takes over; correct',
  superseded: 'a newer message replaced it — correct',
  expires_before_wake: 'quiet hours, the 24 h cap, the cadence or a closed email rail held it past its time — by design; the in-app card still landed',
  no_token: 'no push token — the in-app card still landed',
  opted_out: 'member opted out — correct',
  invalid_token: 'push token no longer valid — the in-app card still landed',
  no_email_copy: 'this stage has no email — by design',
  no_address: 'no email address — a data gap',
  stage_paused: 'stage email paused',
  holdout: 'holdout arm — withheld on purpose',
  suppression_read_failed: 'could not read the member’s email preferences — a fault',
  not_allowlisted: 'not on this environment’s email allowlist — by design off PROD',
  not_a_tester: 'not a tester of this environment — by design off PROD',
  already_sent: 'this stage’s email already went — correct',
  missing_placeholder: 'the email lacked a value it needs — a fault',
  no_unsub_token: 'no unsubscribe token — a fault',
  send_failed: 'the send failed — a fault',
};

export interface EndedRowView {
  key: string;
  railLabel: string;
  outcomeLabel: string;
  /** The plain-English meaning, or the raw outcome when the map has none. Null only for a null outcome. */
  meaning: string | null;
  heldLabel: string;
  /** `state = 'exhausted'` — the rail gave up. The card marks these lines. */
  gaveUp: boolean;
  /** Every raw code, for the tooltip: an operator greps the logs for them. */
  title: string;
  count: number;
}

/** One line of the "Ended without sending" list. */
export function endedRowView(b: ExhaustedBucket): EndedRowView {
  const known =
    b.outcome !== null && Object.prototype.hasOwnProperty.call(OUTBOX_OUTCOME_MEANINGS, b.outcome);
  return {
    key: `${b.rail}:${b.state}:${b.outcome ?? '—'}:${b.reason}`,
    railLabel: humanLabel(b.rail),
    outcomeLabel: b.outcome === null ? OUTBOX_COPY.noOutcome : humanLabel(b.outcome),
    meaning: b.outcome === null ? null : known ? OUTBOX_OUTCOME_MEANINGS[b.outcome] : b.outcome,
    heldLabel: humanLabel(b.reason),
    gaveUp: b.state === 'exhausted',
    title: `${b.rail} / ${b.state} / outcome ${b.outcome ?? 'not recorded'} / deferred ${b.reason}`,
    count: b.count,
  };
}

/**
 * The ended list's rows, or null when the API did not send them.
 *
 * Null is "not reported", which the card says in words. An absent field must
 * not become `[]`, because `[]` prints "Nothing ended without sending." — a
 * statement about a read that did not happen.
 */
export function endedRows(data: Pick<OutboxData, 'ended_24h'> | null | undefined): ExhaustedBucket[] | null {
  return Array.isArray(data?.ended_24h) ? data.ended_24h : null;
}

/**
 * "12 in total · 3 gave up" — both numbers are the API's own totals
 * (`ended_last_24h_total`, `exhausted_last_24h_total`), never re-summed here,
 * so the page cannot become a second place where the counting happens. An
 * absent total prints an em-dash, never a zero.
 */
export function endedTotalLine(
  data: Pick<OutboxData, 'ended_last_24h_total' | 'exhausted_last_24h_total'>,
): string {
  return `${formatCount(data.ended_last_24h_total)} in total · ${formatCount(data.exhausted_last_24h_total)} gave up`;
}
